import { useCallback, useMemo } from "react";
import * as Slider from "@radix-ui/react-slider";
import { LuVolume2, LuVolumeX } from "react-icons/lu";

import { cn } from "@/lib/utils";

export type VolumeControlProps = {
  volume?: number;
  isMuted?: boolean;
  onVolumeChange: (value: number) => void;
  onToggleMute?: () => void;
};

export default function VolumeControl({
  volume = 1,
  isMuted = false,
  onVolumeChange,
  onToggleMute,
}: VolumeControlProps) {
  const normalizedVolume = useMemo(() => {
    if (!Number.isFinite(volume)) return 1;
    return Math.min(Math.max(volume, 0), 1);
  }, [volume]);

  const sliderValue = useMemo(
    () => [Math.round(normalizedVolume * 100)],
    [normalizedVolume],
  );

  const handleSliderChange = useCallback(
    (values: number[]) => {
      const next = values.at(0);
      if (typeof next !== "number") return;
      onVolumeChange(Math.min(Math.max(next / 100, 0), 1));
    },
    [onVolumeChange],
  );

  const handleToggleMute = useCallback(() => {
    if (onToggleMute) {
      onToggleMute();
    } else {
      onVolumeChange(normalizedVolume <= 0.001 ? 1 : 0);
    }
  }, [onToggleMute, onVolumeChange, normalizedVolume]);

  const isEffectivelyMuted = isMuted || normalizedVolume <= 0.001;

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={handleToggleMute}
        className={cn(
          "flex h-10 w-10 items-center justify-center rounded-full text-white transition",
          "hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#43DDA7]",
        )}
        aria-label={isEffectivelyMuted ? "Unmute" : "Mute"}
      >
        {isEffectivelyMuted ? (
          <LuVolumeX className="h-5 w-5" />
        ) : (
          <LuVolume2 className="h-5 w-5" />
        )}
      </button>
      <Slider.Root
        value={sliderValue}
        onValueChange={handleSliderChange}
        min={0}
        max={100}
        step={1}
        aria-label="Volume"
        className="relative flex w-[100px] touch-none items-center cursor-pointer"
      >
        <Slider.Track className="relative h-1 w-full rounded-full bg-white/15">
          <Slider.Range className="absolute h-full rounded-full bg-[#43DDA7]" />
        </Slider.Track>
        <Slider.Thumb className="block h-3 w-3 rounded-full bg-white shadow-[0_0_0_2px_rgba(0,0,0,0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#43DDA7]" />
      </Slider.Root>
    </div>
  );
}
